import { Router } from '@angular/router';
import { Dictionary } from './../../shared/models/classes/dictionary/dictionary';
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UntypedFormBuilder, Validators, UntypedFormGroup, ValidationErrors, UntypedFormControl, UntypedFormArray } from '@angular/forms';
import * as moment from 'moment';
import { UiService } from '@services/ui.service';
import { Order } from '@classes/order/order';
import { OrdersService } from '@services/orders.service';
import { AuthService } from '@services/auth.service';
import { RoleName } from '@enums/role-name.enum';
import { ConfirmValidMatcher } from '@classes/validations/confirm-valid-matcher';
import { Client } from '@classes/client';
import { ClientsService } from '@services/clients.service';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { Address } from '@classes/address';
import { AddressesService } from '@services/addresses.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { MatLegacyAutocomplete as MatAutocomplete, MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import dayjs from 'dayjs';
import { RemindersService } from '@services/reminders.service';
import { AddOrderDocumentRequest } from '@classes/order/add-order-document-request';
import { FileInfo } from '@classes/file/fileInfo';
import { FileService } from '@services/file.service';

@Component({
  selector: 'app-order-create',
  templateUrl: './order-create.component.html',
  styleUrls: ['./order-create.component.scss']
})
export class OrderCreateComponent extends FormEditBaseComponent implements OnInit {

  clients: Client[];
  sites: Site[];
  allTransportTypes: DictionaryValue[];
  urgencyLevels: DictionaryValue[];
  lots: DictionaryValue[];

  senders: Address[];
  receivers: Address[];

  order: Order;

  stringMaxLength = 50;
  commentMaxLength = 500;
  isRestricted: boolean;
  isClient: boolean;
  isAdmin: boolean;
  isAdminWithOneClient: boolean;
  isLoadingLots: boolean;
  mustCheckDate: boolean;
  confirmValidMatcher = new ConfirmValidMatcher();

  invalidFields: string[] = [];
  invalidMessage: string = '';

  transportTypesSelected: DictionaryValue[] = [];
  devices: DictionaryValue[];
  filteredTransportTypes: Observable<DictionaryValue[]>;
  transportTypesCtrl = new UntypedFormControl();
  selectable = true;
  removable = true;
  addOnBlur = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];

  clientForm = new UntypedFormGroup({
    client: new UntypedFormControl('', Validators.required)
  })

  canChooseValue = true;

  file: File = null;

  @ViewChild('deviceInput') deviceInput: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete: MatAutocomplete;

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private authSrv: AuthService,
    private clientsSrv: ClientsService,
    private ordersSrv: OrdersService,
    private sitesSrv: SitesService,
    private dictionarySrv: DictionaryValuesService,
    private addressesSrv: AddressesService,
    private remindersSrv: RemindersService,
    private router: Router,
    private _fileSrv: FileService
  ) {
    super(uiSrv, fb);

  }

  ngOnInit(): void {
    super.ngOnInit();

    // tslint:disable-next-line: max-line-length
    this.isRestricted = this.authSrv.getUser().role.name !== RoleName.super_admin && this.authSrv.getUser().role.name !== RoleName.bizzdev;
    this.isClient = this.authSrv.getUser().role.name === RoleName.client;
    this.isAdmin =
      this.authSrv.getUser().role.name === RoleName.bizzdev ||
      this.authSrv.getUser().role.name === RoleName.super_admin ||
      this.authSrv.getUser().role.name === RoleName.admin;

    if (this.isAdmin) {
      let clients = [];
      clients = this.authSrv.getDecodedToken().client.split(',');
      if (clients.length > 1) {
        this.canChooseValue = false;
        this.isAdminWithOneClient = false;
      } else {
        this.canChooseValue = true;
        this.isAdminWithOneClient = true;
      }
    }

    this.initSubscriptions();
  }

  isControlSet(control: string): boolean {
    return !!this.editForm.get(control);
  }

  onFinish(): void {
    this.editFinished.emit();
    this.router.navigate(['/orders/list']);
  }

  onSubmit(): void {
    this.isLoading = true;
    this.create();
  }

  private create(): void {
    const formValues = this.editForm.value;

    const sitesPromise = new Promise((resolve, reject) => {
      if (typeof formValues.site === 'string') {
        let newSite = new Site(
          formValues.site,
          this.urgencyLevels[0].client,
          false
        );

        this.sitesSrv.post(newSite).subscribe(newSiteFromSrv => {
          formValues.site = newSiteFromSrv;
          resolve(true);
        })
      } else {
        resolve(true);
      }
    });

    Promise.all([sitesPromise])
      .then(() => {
        if (typeof formValues.sender.address === 'string') {
          formValues.sender.otherAddress = formValues.sender.address;
          formValues.sender.address = null;
        } else {
          formValues.sender.otherAddress = null;
        }

        formValues.receivers.map(receiver => {
          if (typeof receiver.address === 'string') {
            receiver.otherAddress = receiver.address;
            receiver.address = null;
          } else {
            receiver.otherAddress = null;
          }

          if (receiver.type === 'isDelivery') {
            receiver.isDelivery = true;
            receiver.isPickup = false;
          } else {
            receiver.isDelivery = false;
            receiver.isPickup = true;
          }

        })

        let callDate = null;

        if (formValues.callDate) {
          let callTime = formValues.callDate.split(':');
          let dateNow = moment(new Date());
          callDate = dateNow.hours(callTime[0]).minutes(callTime[1]).utc().toDate();
        }


        this.order = new Order(
          formValues.site,
          formValues.urgencyLevel,
          this.scheduleToDate(formValues.schedule[0]),
          formValues.ordererPhone,
          formValues.ordererName,
          formValues.sender,
          formValues.receivers,
          this.transportTypesSelected,
          1,
          formValues.ordererComment,
          callDate,
          formValues.lot
        );

        this.ordersSrv.post(this.order)
          .subscribe(
            (orderCreated) => {
              this.isLoading = false;
              this.uiSrv.showSnackbar('orders.create.created', true);
              this.order = orderCreated;

              if (formValues.schedule[0].recallTimer) {
                let recallTimer = dayjs(orderCreated.startDate, { utc: true }).subtract(formValues.schedule[0].recallTimer, 'minute').format();

                this.remindersSrv.post(orderCreated.id, recallTimer).subscribe();
              }

              if (this.file) {
                this._addDocument(this.file);
              }

              this.onFinish();
            },
            () => this.order = null
          );
      });
  }

  private initSubscriptions(): void {
    this.isLoading = true;

    if (this.canChooseValue) {
      const sitesPromise = new Promise((resolve, reject) => {
        this.sitesSrv.getList()
          .subscribe(sitesFromSrv => {
            this.sites = sitesFromSrv;
            resolve(true);
          });
      });

      const clientsPromise = new Promise((resolve) => {
        this.clientsSrv.getList()
          .subscribe(clientsFromSrv => {
            this.clients = clientsFromSrv;
            resolve(true);
          });
      });

      const typeTransportsPromise = new Promise((resolve) => {
        this.dictionarySrv.getTransportList()
          .subscribe(transportsFromSrv => {
            this.allTransportTypes = transportsFromSrv;
            this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
              startWith(null),
              map((fruit: string | null) => fruit ? this._filter(fruit) : this.allTransportTypes.slice()));
            resolve(true);
          });
      });

      const urgencyLevelsPromise = new Promise((resolve) => {
        this.dictionarySrv.getUrgencyList()
          .subscribe(urgencyLevelsFromSrv => {
            this.urgencyLevels = urgencyLevelsFromSrv;
            resolve(true);
          });
      });

      const addressPromise = new Promise((resolve) => {
        this.addressesSrv.getList()
          .subscribe(addressessFromSrv => {
            this.senders = addressessFromSrv.filter(address => address.isSender);
            this.receivers = addressessFromSrv.filter(address => address.isReceiver);
            resolve(true);
          });
      });

      Promise.all([sitesPromise, clientsPromise, typeTransportsPromise, urgencyLevelsPromise, addressPromise])
        .then(() => {
          this.isLoading = false;
          this.initForm();
        });
    } else {
      const clientsPromise = new Promise((resolve) => {
        this.clientsSrv.getList()
          .subscribe(clientsFromSrv => {
            this.clients = clientsFromSrv;
            resolve(true);
          });
      });

      const addressPromise = new Promise((resolve) => {
        this.addressesSrv.getList()
          .subscribe(addressessFromSrv => {
            this.senders = addressessFromSrv.filter(address => address.isSender);
            this.receivers = addressessFromSrv.filter(address => address.isReceiver);
            resolve(true);
          });
      });

      Promise.all([clientsPromise, addressPromise])
        .then(() => {
          this.isLoading = false;
        });
    }
  }


  private initForm(): void {
    this.editForm = this.fb.group({
      ordererComment: [null, [Validators.maxLength(this.commentMaxLength)]],
      urgencyLevel: new UntypedFormControl(null, [Validators.required]),
      lot: new UntypedFormControl(null),
      schedule: this.fb.array([]),
      sender: this.fb.group({
        address: [null, [Validators.required]],
        otherAddress: [null],
        service: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
        ordererComment: [null, [Validators.maxLength(this.commentMaxLength)]],
        isPickup: [true],
        isDelivery: [false]
      }, Validators.required),
      receivers: this.fb.array([], Validators.required),
      ordererPhone: new UntypedFormControl(null, [Validators.maxLength(this.stringMaxLength)]),
      ordererName: new UntypedFormControl(null, [Validators.maxLength(this.stringMaxLength)]),
      callDate: new UntypedFormControl(null),
      site: new UntypedFormControl(null, [Validators.required])
    });

    this.initDateForm();
    this.onAddReceiver();

    this.editForm.valueChanges.subscribe(() => {
      this.invalidFields = this.findInvalidControls();
      this.invalidMessage = 'Le formulaire est incomplet/incorrect';
      this.invalidFields.map(field => {
        this.invalidMessage = this.invalidMessage + ' ' + field;
      })
    })

    this.editForm.get('site').valueChanges.subscribe((site) => {
      this.isLoadingLots = true;
      this.lots = [];
      this.editForm.get('lot').reset();
      if (typeof site !== 'string') {
        this.dictionarySrv.getLotsList(site.id)
          .subscribe(lotsFromSrv => {
            this.lots = lotsFromSrv;
            this.isLoadingLots = false;

            this.editForm.get('lot').setValidators([Validators.required]);

            if (this.lots.length === 0) {
              this.editForm.get('lot').removeValidators(Validators.required);
            }
            else if (this.lots.length === 1) {
              this.editForm.get('lot').setValue(this.lots[0]);
            }
            this.editForm.get('lot').updateValueAndValidity();
          });
      }
    })
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.editForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(this.translateFields(name));
      }
    }

    //check si le dernier receiver est bien une livraison
    let lastReceiver = this.editForm.value.receivers[this.editForm.value.receivers.length - 1];

    if (lastReceiver.type === 'isPickup') {
      invalid.push(' | La dernière étape est un enlèvement');
    }

    if (this.transportTypesSelected.length === 0) {
      invalid.push(' | Il n\'y a aucun type de transport');
    }


    return invalid;
  }

  translateFields(name): string {
    if (name === 'site') {
      return ' | Donneur d\'ordre';
    }
    if (name === 'urgencyLevel') {
      return ' | Niveau d\'urgence';
    }
    if (name === 'sender') {
      return ' | Lieu d\'enlèvement incomplet';
    }
    if (name === 'receivers') {
      return ' | Destinataire(s) incomplet(s)';
    }
    if (name === 'patients') {
      return ' | Patient(s) incomplet(s)';
    }
    if (name === 'lot') {
      return ' | Lot incomplet';
    }
  }

  minTransportType(min = 1): any {
    return (formArray: UntypedFormArray) => {
      return formArray.controls.length >= min ? null : { required: true };
    };
  }

  onAddTransportType(transportType?: Dictionary): void {
    this.transportTypesFields.push(
      this.fb.control(null, [Validators.required])
    );
  }

  removeTransportType(transportTypeToRemove): void {
    this.getArray('transportTypes').controls.splice(this.getArray('transportTypes').controls.indexOf(transportTypeToRemove), 1);
    this.editForm.value.transportTypes.splice(this.editForm.value.transportTypes.indexOf(transportTypeToRemove.value), 1);
  }

  get transportTypesFields(): UntypedFormArray {
    return this.editForm.get('transportTypes') as UntypedFormArray;
  }

  private scheduleToDate(schedule: any): Date {
    if (!schedule.startDate || !schedule.startTime || typeof schedule.startDate === 'string') {
      return null;
    }

    const startDate: moment.Moment = schedule.startDate.local();
    const startTime: number[] = schedule.startTime.split(':').map(s => parseInt(s, 10));
    return startDate.hours(startTime[0]).minutes(startTime[1]).utc().toDate();
  }

  initDateForm(): void {
    const startMoment: moment.Moment = moment(new Date());
    const startTime: string = moment(startMoment).add(5, 'minutes').format('HH:mm');
    const newGroup = this.fb.group({
      startDate: [startMoment, Validators.required],
      startTime: [startTime, Validators.required],
      recallTimer: [30, Validators.min(0)]
    });



    newGroup.controls.startDate.valueChanges
      .subscribe((date) => {
        if (date) {
          this.mustCheckDate = true;
          let dateChoosed = new Date(date).setHours(0, 0, 0, 0);
          let dateNow = new Date().setHours(0, 0, 0, 0);

          if (dateChoosed < dateNow) {
            let confirmation = confirm("Attention, date de prise en charge antérieure, voulez-vous continuer ?");
            if (!confirmation) {
              setTimeout(() => {
                newGroup.get('startDate').reset();
              })
            }
          }
        }
      });

    newGroup.controls.startTime.valueChanges
      .subscribe(() => {
        this.mustCheckDate = true;
      });


    this.getArray('schedule').push(newGroup);
  }

  private minDateValidator(control: UntypedFormGroup): ValidationErrors | null {
    const scheduleDate = this.scheduleToDate(control.value);
    if (!this.mustCheckDate) {
      return null;
    }

    if (scheduleDate && scheduleDate < new Date()) {
      this.editForm.setErrors({ mismatch: true });
      return { mismatch: true };
    }

    return null;
  }

  resetStartDate(control) {
    control.get('startDate').reset();
  }

  resetBirthDate(control) {
    control.get('birthdate').reset();
  }

  onAddPatient(indexReceiver: number): void {

    const newGroup = this.fb.group({
      firstname: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      birthname: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [null, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [null, [Validators.required]],
      productType: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [null, [Validators.required]],
      ipp: [null, Validators.maxLength(this.stringMaxLength)]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  onCopyPatient(indexReceiver: number, control) {
    let patient = control.value;

    const newGroup = this.fb.group({
      firstname: [patient.firstname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      birthname: [patient.birthname, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      lastname: [patient.lastname, [Validators.maxLength(this.stringMaxLength)]],
      birthdate: [patient.birthdate, [Validators.required]],
      productType: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      quantity: [null, [Validators.required]],
      ipp: [patient.ipp, Validators.maxLength(this.stringMaxLength)]
    });

    this.getArrayPatients(indexReceiver).push(newGroup);
  }

  getArrayPatients(index?: number) {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;
    let patients = receivers.controls[index].get('patients') as UntypedFormArray;
    return patients;
  }

  onRemovePatient(indexReceiver: number, indexPatient: number): void {
    this.getArrayPatients(indexReceiver).removeAt(indexPatient);
  }

  isControlPatientSet(index: number): boolean {
    let receivers = this.editForm.get('receivers') as UntypedFormArray;

    return !!receivers.controls[index].get('patients');
  }

  onAddReceiver(): void {

    const newGroup = this.fb.group({
      address: [null, [Validators.required]],
      otherAddress: [null],
      service: [null, [Validators.required, Validators.maxLength(this.stringMaxLength)]],
      ordererComment: [null, [Validators.maxLength(this.commentMaxLength)]],
      type: ['isDelivery', [Validators.required]],
      patients: this.fb.array([])
    });

    newGroup.get('type').valueChanges.subscribe(result => {
      if (result === 'isDelivery') {
        newGroup.addControl('patients', this.fb.array([]));
      } else {
        newGroup.removeControl('patients');
      }
    })

    this.getArray('receivers').push(newGroup);
  }

  onRemoveReceiver(index: number): void {
    this.getArray('receivers').removeAt(index);
  }

  onCheckboxChange(e): any {
    const checkArray: UntypedFormArray = this.editForm.get('transportTypes') as UntypedFormArray;

    if (e.checked) {
      checkArray.push(new UntypedFormControl(e.source.value));
    } else {
      let i: number = 0;
      checkArray.controls.forEach((item: UntypedFormControl) => {
        if (item.value === e.source.value) {
          checkArray.removeAt(i);
          return;
        }
        i++;
      });
    }
  }

  remove(fruit: DictionaryValue): void {
    const index = this.transportTypesSelected.indexOf(fruit);

    if (index >= 0) {
      this.transportTypesSelected.splice(index, 1);
    }

    this.checkIfSelected();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    document.getElementById("deviceChipList").blur();
    if (!this.transportTypesSelected.includes(event.option.value)) {
      this.transportTypesSelected.push(event.option.value);
      // this.devices = this._filter(event.option.value);
      this.checkIfSelected();

      this.deviceInput.nativeElement.value = '';
    }
  }

  checkIfSelected() {
    this.filteredTransportTypes = this.filteredTransportTypes.pipe(
      map(array => {
        return array.map(element => {
          if (this.transportTypesSelected.includes(element)) {
            element.selected = true;
          } else {
            element.selected = false;
          }
          return element;
        })

      })
    )
  }

  private _filter(value?): DictionaryValue[] {
    let filterValue = '';

    if (value.name) {
      filterValue = value.name.toLowerCase();
      return this.allTransportTypes.filter(el => {
        return el.id === value.id;
      });
    } else {
      filterValue = value.toLowerCase();
      return this.allTransportTypes.filter(transportType => transportType.name.toLowerCase().indexOf(filterValue) === 0);
    }
  }

  clearDevicesSelected(): void {
    this.transportTypesSelected = [];
    this.filteredTransportTypes.pipe(
      map(fruit => {
        return this.allTransportTypes;
      })
    )
    this.checkIfSelected();
  }

  getClientControl(control: string): UntypedFormControl {
    return this.clientForm.get(control) as UntypedFormControl;
  }

  chooseClient() {
    this.isLoading = true;
    let clientId = this.clientForm.value.client.id;
    let clientArray = [clientId];
    this.sites = [];
    this.lots = [];

    if (this.editForm) {
      this.editForm.get('urgencyLevel').setValue(null);
      this.editForm.get('lot').setValue(null);
    }

    if (this.transportTypesSelected.length > 0) {
      this.transportTypesSelected = [];
    }

    const sitesPromise = new Promise((resolve, reject) => {
      this.sitesSrv.getListByClients(clientArray)
        .subscribe(sitesFromSrv => {
          this.sites = sitesFromSrv;
          resolve(true);
        });
    });

    const typeTransportsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getTransportListById2(clientId)
        .subscribe(transportsFromSrv => {
          this.allTransportTypes = transportsFromSrv;
          this.filteredTransportTypes = this.transportTypesCtrl.valueChanges.pipe(
            startWith(null),
            map((fruit: string | null) => fruit ? this._filter(fruit) : this.allTransportTypes.slice()));
          resolve(true);
        });
    });

    const urgencyLevelsPromise = new Promise((resolve, reject) => {
      this.dictionarySrv.getUrgencyListById2(clientId)
        .subscribe(urgencyLevelsFromSrv => {
          this.urgencyLevels = urgencyLevelsFromSrv;
          resolve(true);
        });
    });


    Promise.all([sitesPromise, typeTransportsPromise, urgencyLevelsPromise])
      .then(() => {
        this.isLoading = false;
        this.canChooseValue = true;
        this.initForm();
      });
  }

  public onUploadFile(event: any): void {
    if (event.target.files.length > 0) {
      this.file = event.target.files[0];
    }
  }

  public removeFile() {
    this.file = null;
  }

  private async _addDocument(file: File) {
    const fileInfo = await this._postFile(file);
    const fileName = this._fileNameWithoutExtension(file.name);
    const addDocumentRequest = new AddOrderDocumentRequest(fileInfo, fileName);
    this._postDocument(addDocumentRequest);
  }

  private _fileNameWithoutExtension(fileName: string): string {
    return fileName.substring(0, fileName.lastIndexOf('.'));
  }

  private _postFile(file: File): Promise<FileInfo> {
    return new Promise((resolve) => {
      this._fileSrv.postFile(file).subscribe((fileInfo: FileInfo) => {
        resolve(fileInfo);
      });
    });
  }

  private _postDocument(addDocumentRequest: AddOrderDocumentRequest): void {
    this.ordersSrv.postDocument$(this.order.id, addDocumentRequest).subscribe({
      next: doc => {

      },
      error: err => {
        console.error('error', err);
      }
    });
  }
}
